import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Spinner, ErrorBoundary } from '@mbbe/ui';
import { usePageTracking } from './hooks/usePageTracking';
import { useAttributionCapture } from './hooks/useAttributionCapture';
import Layout from './components/Layout';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { QuoteModalProvider } from './features/quote/QuoteModalContext';
import QuoteRoute from './features/quote/QuoteRoute';
import { trackedPortfolioProjectId } from './features/portfolio/tracking';

const Home = React.lazy(() => import('./pages/Home'));
// Platform expansion modules own their nested routes (docs/platform-expansion-20261015.md).
const PortfolioRoutes = React.lazy(() => import('./features/portfolio/PortfolioRoutes'));
const BlogRoutes = React.lazy(() => import('./features/blog/BlogRoutes'));
const DemoRoutes = React.lazy(() => import('./features/demos/DemoRoutes'));
const ShareRoutes = React.lazy(() => import('./features/share/ShareRoutes'));
const Services = React.lazy(() => import('./pages/Services'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const Contact = React.lazy(() => import('./pages/Contact'));
const KvkkDisclosure = React.lazy(() => import('./pages/KvkkDisclosure'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const DesignSystem = React.lazy(() => import('./pages/DesignSystem'));

function AppRoutes() {
  const location = useLocation();
  const projectId = trackedPortfolioProjectId(location.pathname);

  usePageTracking(projectId);
  useAttributionCapture();

  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen bg-dark-900">
          <Spinner size="lg" className="text-brand-500" />
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="portfolio/*" element={<PortfolioRoutes />} />
          <Route path="blog/*" element={<BlogRoutes />} />
          <Route path="demos/*" element={<DemoRoutes />} />
          <Route path="paylasim/*" element={<ShareRoutes />} />
          <Route path="services" element={<Services />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="contact" element={<Contact />} />
          <Route path="teklif-al" element={<QuoteRoute />} />
          <Route path="kvkk-aydinlatma-metni" element={<KvkkDisclosure />} />
          <Route path="gizlilik-politikasi" element={<PrivacyPolicy />} />
          <Route path="hizmet-sozlesmesi" element={<LegalPage slug="hizmet-sozlesmesi" />} />
          <Route path="iptal-iade-politikasi" element={<LegalPage slug="iptal-iade-politikasi" />} />
          <Route path="kullanim-kosullari" element={<LegalPage slug="kullanim-kosullari" />} />
          <Route path="design-system" element={<DesignSystem />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </React.Suspense>
  );
}

function App() {
  const [consentHeight, setConsentHeight] = React.useState(0);
  // No timer-driven startup overlay: routes render as soon as their lazy chunk
  // resolves and the Suspense fallback in AppRoutes covers only real pending work.
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <QuoteModalProvider>
          <CookieConsentBanner onHeightChange={setConsentHeight} />
          <div
            className={consentHeight ? 'h-[calc(100dvh-var(--consent-height))] overflow-y-auto sm:h-auto sm:overflow-visible' : undefined}
            style={{ '--consent-height': `${consentHeight}px` } as React.CSSProperties}
          >
            <AppRoutes />
          </div>
        </QuoteModalProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
